import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
// Same local pages.json source as Header — inherited SitemapContent imported
// the parent theme's pages.json, so URLs (e.g. /contact vs /contact-us) and
// visibility did not match the pages actually shown in nav.
import pagesData from '../pages.json';
import { isUsableUrl } from '../utils/addressUrl';

interface PageEntry {
  name?: string;
  path?: string;
  position?: number;
  visible?: boolean;
  template?: string;
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  rating?: number | string | null;
  review_text?: string | null;
}

interface SitemapItem {
  label: string;
  href: string;
}

/**
 * Build sitemap links from this repo's pages.json — same visibility, order,
 * and path-default rules as Header navigation (`path` or `/${alias}`).
 */
const SITEMAP_ITEMS: SitemapItem[] = Object.entries(pagesData as Record<string, PageEntry>)
  .filter(([alias, page]) => {
    if (alias === 'home' || page.path === '/') return false;
    if (page.visible === false) return false;
    if (page.template) return false;
    const href = page.path ?? `/${alias}`;
    if (href === '/sitemap') return false;
    return true;
  })
  .sort((a, b) => (a[1].position ?? 0) - (b[1].position ?? 0))
  .map(([alias, page]) => ({
    label: page.name ?? alias,
    href: page.path ?? `/${alias}`,
  }));

export function SitemapContent() {
  // Hide /virtual-tour unless the property has at least one tour with a
  // usable URL (matches Header / VirtualTourEmbed), and hide /reviews
  // unless the property actually has reviews.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', {
    collection: 'reviews',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const hasReviews = (reviews || []).length > 0;

  const pages = SITEMAP_ITEMS.filter((item) => {
    if (item.href === '/virtual-tour' && !hasVirtualTours) return false;
    if (item.href === '/reviews' && !hasReviews) return false;
    return true;
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.href}
              href={page.href}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.label}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews (visibility check)',
      collection: 'reviews',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
